import { Link } from 'react-router-dom';
import { useNotificationStore } from '../stores/notificationStore';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  X,
  ExternalLink
} from 'lucide-react';

// Icon mapping
const ICONS = {
  check: CheckCircle,
  x: XCircle,
  alert: AlertTriangle,
  info: Info,
  plus: CheckCircle
};

// Color classes
const COLORS = {
  emerald: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    icon: 'text-emerald-400',
    title: 'text-emerald-300'
  },
  red: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    icon: 'text-red-400',
    title: 'text-red-300'
  },
  amber: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    icon: 'text-amber-400',
    title: 'text-amber-300'
  },
  primary: {
    bg: 'bg-primary-500/10',
    border: 'border-primary-500/30',
    icon: 'text-primary-400',
    title: 'text-primary-300'
  }
};

function Toast({ notification, onDismiss }) {
  const config = notification.config || {};
  const colorKey = config.color || 'primary';
  const iconKey = config.icon || 'info';
  
  const colors = COLORS[colorKey] || COLORS.primary;
  const Icon = ICONS[iconKey] || Info;
  
  // Get link based on notification type
  const getDetailLink = () => {
    if (notification.details?.tradeId) {
      return '/copy-configs'; // Link to copy configs page with trade activity
    }
    if (notification.details?.positionId) {
      return '/positions';
    }
    return null;
  };
  
  const detailLink = getDetailLink();
  
  return (
    <div 
      className={`
        relative w-80 p-4 rounded-xl border shadow-2xl backdrop-blur-sm
        ${colors.bg} ${colors.border}
        animate-slide-in-right
        transition-all duration-300 ease-out
      `}
      role="alert"
    >
      {/* Dismiss button */}
      <button
        onClick={() => onDismiss(notification.id)}
        className="absolute top-2 right-2 p-1 rounded-lg hover:bg-white/10 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4 text-dark-400 hover:text-white" />
      </button>
      
      <div className="flex items-start gap-3 pr-6">
        {/* Icon */}
        <div className={`flex-shrink-0 mt-0.5 ${colors.icon}`}>
          <Icon className="w-5 h-5" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`font-medium text-sm ${colors.title}`}>
            {notification.title}
          </p>
          
          {notification.message && (
            <p className="text-xs text-dark-300 mt-1 line-clamp-2">
              {notification.message}
            </p>
          )}
          
          {/* Footer with time and link */}
          <div className="flex items-center justify-between mt-2">
            <span className="text-[0.65rem] text-dark-500">
              {new Date(notification.createdAt).toLocaleTimeString()}
            </span>
            
            {detailLink && (
              <Link
                to={detailLink}
                onClick={() => onDismiss(notification.id)}
                className="text-[0.65rem] text-primary-400 hover:text-primary-300 flex items-center gap-1"
              >
                View details <ExternalLink className="w-3 h-3" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function NotificationToast() {
  const { notifications, dismissNotification, dismissAll } = useNotificationStore();
  
  // Don't render if no notifications
  if (notifications.length === 0) {
    return null;
  }
  
  return (
    <div 
      className="fixed top-4 right-4 z-50 flex flex-col gap-3"
      aria-live="polite"
      aria-label="Notifications"
    >
      {/* Dismiss all button if multiple */}
      {notifications.length > 1 && (
        <button
          onClick={dismissAll}
          className="self-end text-xs text-dark-400 hover:text-white transition-colors px-2 py-1 rounded hover:bg-dark-700"
        >
          Dismiss all ({notifications.length})
        </button>
      )}
      
      {/* Toast stack */}
      {notifications.map((notification, index) => (
        <div
          key={notification.id}
          style={{
            // Slight offset for stacked effect
            transform: `translateY(${index * 2}px)`,
            zIndex: 50 - index
          }}
        >
          <Toast
            notification={notification}
            onDismiss={dismissNotification}
          />
        </div>
      ))}
    </div>
  );
}
